import { FC, useEffect } from 'react'

import { DocumentFile, DocumentType, DocumentWithTypeFile } from 'modules/domain/types'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { uploadDocuments } from 'modules/domain/common/managers'
import { AFilesByTypes } from 'views/components/FilesByTypes/AFilesByTypes'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { Progress } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'

type Props = {
  setSummary?: (arg: number) => void
  carrierId: string
  documentsRefetch: refetchFunc
  documents: DocumentFile[]
  documentsProgress: Progress
}

export const CarrierContractDocuments: FC<Props> = ({
  carrierId,
  setSummary,
  documentsRefetch,
  documents,
  documentsProgress,
}) => {
  const [, documentTypes = []] = useSingleEntity<DocumentType[]>(endpoints.LogisticsKR.carrierContractTypes(), false)

  useEffect(() => {
    if (documentsProgress === Progress.SUCCESS && documentTypes.length > 0 && setSummary) {
      setSummary(
        documentTypes.reduce((acc, type) => {
          const documentsOfType = documents.filter((document) => document.document_type === type.slug)
          return acc + documentsOfType.length
        }, 0),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents.length, documentTypes])

  const [uploadProgress, upload] = useAProgress<DocumentWithTypeFile[]>(
    (documentType: DocumentType['slug'], files: File[]) => {
      const promises: Promise<DocumentWithTypeFile>[] = []
      for (const file of files) {
        promises.push(uploadDocuments(endpoints.LogisticsKR.carrierDocuments(carrierId), documentType, file))
      }
      return Promise.all(promises)
    },
    {
      eventName: 'uploadDocument',
      onSuccess: () => documentsRefetch(),
    },
  )

  const [removeProgress, removeDoc] = useAProgress(
    (documentId: string) => apiClient.delete(endpoints.LogisticsKR.carrierDocument(carrierId, documentId)),
    {
      eventName: 'deleteDocument',
      onSuccess: () => documentsRefetch(),
    },
  )

  return (
    <>
      <AFilesByTypes
        types={documentTypes}
        files={documents}
        uploadProgress={uploadProgress}
        onUpload={upload}
        onDelete={removeDoc}
        deleteProgress={removeProgress}
      />
    </>
  )
}
